import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "wouter";
import {
  ASSIGNMENT_OVERRIDE_REASONS,
  assignmentOverrideInputSchema,
  GAP_GUIDANCE,
  INTAKE_GAPS,
  type AssignmentOverrideReason,
  type IntakeGap,
  type Role,
} from "@titleverify/shared";
import {
  apiErrorText,
  apiFetch,
  type Case,
  type RegistryOffice,
  type RouteResult,
} from "../api.js";
import { useAuth } from "../auth.js";
import { LoadFailure } from "../components/LoadFailure.js";
import { useMe } from "../me.js";
import { CaseDocuments } from "../components/CaseDocuments.js";
import { CaseMeetings } from "../components/CaseMeetings.js";
import { CaseProgress } from "../components/CaseProgress.js";
import { SupplementCase } from "../components/SupplementCase.js";
import { formatDate, isAssignableCaseState, isRoutableCaseState } from "./caseDisplay.js";

/**
 * ONE CASE, SEEN BY WHOEVER IS ENTITLED TO SEE IT.
 * ===========================================================================
 *
 * The same URL for all three viewers, because the server already decides who
 * gets what: `getCaseForActor` looks the row up and then runs it through
 * `authorize(case:read)`. A client sees their own case, an assigned professional
 * sees theirs, an admin sees any. Nobody else gets a body at all.
 *
 * The ops controls are rendered for an admin only. That is a COURTESY, NOT
 * ENFORCEMENT — both endpoints live under /api/admin and refuse every other role
 * at the chokepoint regardless of what this file draws. What the hiding buys is
 * that a client is never shown anything resembling a control over which attorney
 * gets their case, which is the shape COMPLIANCE Constraint 1 forbids.
 *
 * 404 AND 403 ARE NOT THE SAME THING HERE and the copy must not merge them. The
 * row is looked up BEFORE authorize, so a non-existent id gives 404 and an
 * existing-but-not-yours id gives 403. Rendering 403 as "no such case" would be
 * inventing a fact — and the opposite, explaining the 403, would confirm that
 * someone else's case exists, which is exactly why the server sends no reason.
 */
export function CaseDetail({ params }: { params: { id: string } }) {
  const { getToken } = useAuth();
  const me = useMe();
  const caseId = params.id;

  const caseQuery = useQuery({
    queryKey: ["case", caseId],
    queryFn: async () => apiFetch<Case>(`/cases/${caseId}`, { token: await getToken() }),
  });

  if (caseQuery.isPending || me.isPending) return <p className="muted" role="status">Loading the case…</p>;

  if (caseQuery.error) {
    return (
      <section>
        <BackLink role={me.data?.role} />
        <LoadFailure error={caseQuery.error} fallback="Could not load this case.">
          <p>
            <small>
              The case itself is unaffected — this is the page failing to fetch it. If it is
              a request of your own, it is still listed on your account page.
            </small>
          </p>
        </LoadFailure>
      </section>
    );
  }

  /*
   * THE ROLE FAILING TO LOAD IS A FAILURE OF THIS PAGE, NOT A SMALLER VERSION OF IT.
   *
   * Almost everything below is gated on `role`, and an unhandled `me.error` left
   * every one of those gates false: no documents, no supplement form, no
   * meetings, no ops controls, no error either — and `showStatus` (role !==
   * "client") flipping TRUE, so the one reader the CaseProgress panel exists for
   * was shown the raw status token it exists to replace. The page rendered as if
   * that were all there is on this case.
   *
   * A screen that quietly omits what it could not load is stating something
   * false about the case by omission, which is non-negotiable 1 arriving through
   * the back door. So this stops, and says which of the two requests failed.
   */
  if (me.error) {
    return (
      <section>
        <BackLink role={undefined} />
        <p role="alert">{apiErrorText(me.error, "Could not load your account.")}</p>
        <div className="caveat">
          <p>
            <strong>The case is not shown, and that is deliberate.</strong> What this screen
            shows depends on who is looking at it — a client sees the progress of their
            request and the documents held on it, the assigned attorney sees the case as they
            work it, an operator sees the routing and assignment controls. The request that
            establishes which of those you are is the one that failed, so the page cannot
            show any of them.
          </p>
          <p>
            {/*
              The distinction is worth spelling out: the case request may well
              have succeeded. Letting a reader assume their case is the thing
              that broke would be inventing a cause, which is the same fault as
              the silent degradation above, pointed the other way.
            */}
            <strong>Nothing about the case itself has changed.</strong> This is your account
            failing to load, not the case. Reloading is the right first move; if it keeps
            happening it needs an operator, not you.
          </p>
        </div>
      </section>
    );
  }

  const caseRow = caseQuery.data;
  const role = me.data?.role;

  return (
    <section className={role === "admin" ? "wide" : undefined}>
      <BackLink role={role} />

      <header className="page-head">
        <p className="eyebrow">Verification request</p>
        <h2>Case</h2>
        <p className="lede">
          <code>{caseRow.id}</code>
        </p>
      </header>

      {/*
        The client gets the plain-language version of the status — what the state
        means and whose move it is — and therefore does NOT get the raw token
        repeated in the list below, which is why `showStatus` is false for them.
        An attorney and an operator get the token, because it is the register
        they work in and the CASE_NEXT_STEP strings are written in the client's
        second person ("nothing is needed from you"), which reads as nonsense to
        the person actually doing the work.
      */}
      {role === "client" ? <CaseProgress caseRow={caseRow} /> : null}

      <WhatIsKnown caseRow={caseRow} showStatus={role !== "client"} />
      <WhatIsNotKnown caseRow={caseRow} />

      {/*
        Documents are mounted for the two viewers the server grants every kind on
        their own case, and deliberately NOT for an admin.

        An admin has no `document:read` rule at all — ARCHITECTURE §9 keeps staff
        out of client documents, and the break-glass path does not exist yet — so
        an admin receives an empty list for EVERY case, always. Rendering the
        panel here would print "nothing is held on this case" over cases that are
        full of deeds. That is a fabricated fact about every case in the system,
        which is precisely non-negotiable 1.
      */}
      {role === "client" ? <CaseDocuments caseId={caseRow.id} viewer="client" /> : null}
      {role === "professional" ? (
        <CaseDocuments caseId={caseRow.id} viewer="professional" />
      ) : null}
      {role === "admin" ? <DocumentsWithheldFromOps /> : null}

      {/*
        The client's way out of NEEDS_INFO, here as well as on their case card,
        because this page is linked as "see everything on this case" and a
        client who followed that link to find out what is holding their request
        up should be able to answer it where they read it.

        CLIENT ONLY. The server's `case:update` grant is wider — an assigned
        professional or search agent would pass authorize() — but a NEEDS_INFO
        case has no assignments at all (both assignment columns stay null
        through the whole intake phase), so those roles are structurally denied
        on precisely the cases this form appears for. Drawing it for them would
        offer a control that could only ever 403. The admin grant IS
        unconditional, and no control is drawn for them either: ops correcting a
        client's account of their own land, with no record of who supplied what,
        is not a thing this platform should make easy.
      */}
      {role === "client" ? <SupplementCase caseRow={caseRow} /> : null}

      {/*
        MEETINGS — mounted for the case's two participants and NOBODY ELSE.
        ===================================================================
        This is not the usual "hide what they cannot use" courtesy. The
        `meeting:participate` chokepoint grants `client` (isOwnCase) and
        `professional` (isAssignedProfessional) and stops there, and the gate
        test refuses an admin with ForbiddenError under an explicit comment:
        the platform coordinates the case but does not arrange the meeting,
        because a meeting the platform brokered would imply it is soliciting on
        the attorney's behalf (COMPLIANCE Constraint 1).

        So there is deliberately no ops view of this and no ops control over it —
        not a read-only one either. `search_agent` is not granted the rule and
        has no surface at all.
      */}
      {role === "client" || role === "professional" ? (
        <CaseMeetings
          caseId={caseRow.id}
          viewerParty={role === "client" ? "client" : "professional"}
          assignedProfessionalId={caseRow.assignedProfessionalId}
        />
      ) : null}

      {role === "admin" ? <OpsActions caseRow={caseRow} /> : null}
    </section>
  );
}

function BackLink({ role }: { role: Role | undefined }) {
  return (
    <p className="breadcrumb">
      {role === "admin" ? (
        <Link href="/admin/cases">← Case queue</Link>
      ) : (
        <Link href="/">← Your account</Link>
      )}
    </p>
  );
}

/** The facts the platform actually holds about this case, and only those. */
function WhatIsKnown({ caseRow, showStatus }: { caseRow: Case; showStatus: boolean }) {
  const { getToken } = useAuth();
  const me = useMe();

  /*
   * The office NAME behind the routing code, fetched only for an admin — it is
   * the one role the registry endpoint answers for. A client's or professional's
   * request would 403, so asking would mean rendering an error about a name that
   * is decoration on their screen anyway. They see the code, which is what their
   * case payload actually carries.
   */
  const offices = useQuery({
    queryKey: ["admin", "registry-offices"],
    queryFn: async () =>
      apiFetch<{ registryOffices: RegistryOffice[] }>("/admin/registry-offices", {
        token: await getToken(),
      }),
    enabled: me.data?.role === "admin",
  });

  const officeName =
    caseRow.registryDistrict === null
      ? null
      : (offices.data?.registryOffices.find((o) => o.code === caseRow.registryDistrict)?.name ??
        null);

  return (
    <dl>
      {showStatus ? (
        <>
          <dt>Progress</dt>
          <dd>
            {/*
              A workflow state gets .state — monospace and neutral. The .status-*
              palette is a judgement about a person's verification standing and
              borrowing it here would tell the reader the platform thinks a case at
              this step is good or bad news. It has no such view.
            */}
            <span className="state">{caseRow.status}</span>
          </dd>
        </>
      ) : null}

      <dt>Registry office with jurisdiction</dt>
      <dd>
        {caseRow.registryDistrict === null ? (
          "Not yet resolved."
        ) : officeName ? (
          <>
            {officeName} (<code>{caseRow.registryDistrict}</code>)
          </>
        ) : (
          <code>{caseRow.registryDistrict}</code>
        )}
      </dd>

      <dt>Land system</dt>
      <dd>
        {caseRow.landSystem ??
          "Not yet established. Whether the parcel is under deed registration or title " +
            "registration is settled by the attorney on the case — routing does not set it, and " +
            "the platform does not infer it."}
      </dd>

      <dt>Attorney</dt>
      <dd>
        {caseRow.assignedProfessionalId
          ? "An attorney-at-law is assigned to this case."
          : "Nobody is assigned to it at the moment."}
      </dd>

      <dt>Opened</dt>
      <dd>{formatDate(caseRow.createdAt)}</dd>

      <dt>Last activity</dt>
      <dd>{formatDate(caseRow.updatedAt)}</dd>
    </dl>
  );
}

/**
 * WHAT IS MISSING, SAID OUT LOUD.
 *
 * Two different kinds of not-known, and collapsing them would be dishonest in
 * opposite directions:
 *
 *  1. THE OUTSTANDING GAPS. Real, server-derived, and the client's to close. Only
 *     shown when the case is actually at NEEDS_INFO — the `needsInfoGaps` column
 *     is not cleared on the way out of that state, so rendering it unconditionally
 *     would show a ROUTED case a list of things it no longer needs.
 *
 *  2. THE PARCEL DETAILS, which this screen CANNOT show. The platform holds them
 *     — the client gave them at intake and routing reads them — but no endpoint
 *     returns a parcel to any role. That is a gap in what has been BUILT, not a
 *     statement that the case lacks the details, and the difference matters
 *     enormously to an operator deciding whether a case is ready. Saying nothing
 *     would let a blank screen imply the first meaning.
 */
function WhatIsNotKnown({ caseRow }: { caseRow: Case }) {
  const gaps = readGaps(caseRow.needsInfoGaps);

  return (
    <>
      {caseRow.status === "NEEDS_INFO" ? (
        <div className="notice">
          <h3>Waiting on the client</h3>
          {gaps === null ? (
            <p>
              This case is marked as needing more information, but the list of
              what is outstanding did not arrive in a form this screen can read.
              The case is genuinely at NEEDS_INFO; what is missing from the
              display is the reason, not the case.
            </p>
          ) : gaps.length === 0 ? (
            <p>
              This case is at NEEDS_INFO with no outstanding items recorded
              against it. That combination is not something this screen can
              explain — it needs an operator to look at, not a guess from here.
            </p>
          ) : (
            <ul>
              {gaps.map((gap) => (
                <li key={gap.code}>
                  {/*
                    The wording comes from GAP_GUIDANCE in packages/shared — the
                    same text the API sends the client at intake — so an attorney
                    can correct it in one place rather than in two that drift.
                  */}
                  {gap.guidance ?? (
                    <>
                      <code>{gap.code}</code> — this platform has no explanation
                      recorded for that code.
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : null}

      <div className="caveat">
        <p>
          <strong>The parcel&rsquo;s own details are not shown on this screen.</strong> The
          district, DS division, survey plan and lot, extent and deed references were given
          at intake and the platform holds them — routing reads the district to work out
          jurisdiction. But no part of the API returns a parcel to any role, so there is
          nothing here to display. Read this as a screen that has not been built, not as a
          case with details missing.
        </p>
      </div>
    </>
  );
}

/** Why an ops screen shows no documents — said plainly, so silence is not read as emptiness. */
function DocumentsWithheldFromOps() {
  return (
    <div className="caveat">
      <p>
        <strong>Documents are not listed for operations staff.</strong> An admin has no
        read permission over case documents, so this screen is not able to tell you
        whether anything is held on this case — not that nothing is. The client and the
        assigned attorney see the papers; ops does not.
      </p>
    </div>
  );
}

/**
 * Narrows the `unknown` JSONB the API sends into gaps we can render.
 *
 * `needsInfoGaps` is typed `unknown` in api.ts for an honest reason: it is a
 * JSONB column and nothing in the response contract guarantees its shape to the
 * browser. So this validates rather than casts, and returns null when the value
 * is not an array at all — which the caller renders as "the reason did not
 * arrive", not as "there are no gaps". A cast here would turn a malformed
 * payload into a confident empty list.
 *
 * A code that is not in INTAKE_GAPS is kept and shown as a bare code rather than
 * dropped. A gap the client must close is not something to silently discard
 * because this build of the frontend has not heard of it.
 */
function readGaps(value: unknown): { code: string; guidance: string | null }[] | null {
  if (!Array.isArray(value)) return null;
  return value
    .filter((entry): entry is string => typeof entry === "string")
    .map((code) => ({
      code,
      guidance: (INTAKE_GAPS as readonly string[]).includes(code)
        ? GAP_GUIDANCE[code as IntakeGap]
        : null,
    }));
}

/**
 * The acts an operator can drive on a case today: route it, and put an attorney
 * on it — the second through two doors, the mechanical rotation and the declared
 * override, which `AssignAction` keeps visibly apart.
 *
 * ONLY THESE TWO EXIST. The case state machine has a dozen other legal edges —
 * start search, review, opinion, package, deliver, close — and NONE of them has
 * an endpoint yet (they are MD5/MD6). No control for them is drawn here, because
 * a button that 404s is worse than an absent one: it tells an operator the
 * platform can do something it cannot.
 */
function OpsActions({ caseRow }: { caseRow: Case }) {
  return (
    <section className="stack">
      <h3>Operations</h3>
      {isRoutableCaseState(caseRow.status) ? <RouteAction caseRow={caseRow} /> : null}
      {isAssignableCaseState(caseRow.status) ? <AssignAction caseRow={caseRow} /> : null}
      {!isRoutableCaseState(caseRow.status) && !isAssignableCaseState(caseRow.status) ? (
        <p className="empty">
          There is nothing to do on this case from here. Routing and assignment are
          the only two acts this platform has built, and a case at {caseRow.status} is
          past both of them. Everything after assignment — the search, the opinion,
          packaging and delivery — has no screen yet.
        </p>
      ) : null}
    </section>
  );
}

/**
 * ROUTING: one button, no form, and that is the whole point.
 *
 * THE OPERATOR DOES NOT CHOOSE THE REGISTRY, and there is deliberately no field
 * through which they could. Routing keys off the PARCEL — the search happens at
 * the registry with jurisdiction over the LAND, not where the client lives and
 * not where the attorney practises (CLAUDE.md §5). The endpoint reads no body at
 * all; `resolveRegistryOffice` reads the parcel's district and nothing about any
 * person involved.
 *
 * A picker here would be the platform fabricating a routing key. The cost of
 * getting it wrong is not abstract: a case routed to an office with no
 * jurisdiction over the parcel sends an attorney on a wasted trip to a registry
 * that has never heard of the land, and bills the client for it. So the
 * consequence panel says out loud what decides the routing, because the trap is
 * an operator ASSUMING they were supposed to pick and reading the absence of a
 * dropdown as a missing feature.
 */
function RouteAction({ caseRow }: { caseRow: Case }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [result, setResult] = useState<RouteResult | null>(null);

  const route = useMutation({
    mutationFn: async () =>
      apiFetch<RouteResult>(`/admin/cases/${caseRow.id}/route`, {
        token: await getToken(),
        method: "POST",
      }),
    onSuccess: (data) => {
      setResult(data);
      void queryClient.invalidateQueries({ queryKey: ["case", caseRow.id] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "cases"] });
    },
  });

  return (
    <div className="stack">
      <div className="consequence">
        <h3>Routing this case</h3>
        <p>
          Routing works out which land registry has jurisdiction over{" "}
          <strong>the parcel</strong>, by matching the district the client gave at intake
          against the registry-office table. It is decided by{" "}
          <strong>where the land is registered</strong> — not where the client lives, and
          not where any attorney practises.
        </p>
        <p>
          <strong>There is nothing here to choose.</strong> This takes no input from you,
          which is why there is no registry to pick: the office comes from the parcel or it
          is not resolved at all. You can review and manage the offices it matches against
          on the <Link href="/admin/registry">registry offices</Link> screen.
        </p>
        <p>
          If an office resolves, the case moves to ROUTED and can then have an attorney
          assigned. If one does not, the case sits at VALIDATING for ops and{" "}
          <strong>nothing is guessed</strong> — no district is written, and the client is
          not asked for anything.
        </p>
      </div>

      {route.error ? (
        <p role="alert">{apiErrorText(route.error, "Could not route this case.")}</p>
      ) : null}

      {result === null ? (
        <div className="actions">
          <button type="button" disabled={route.isPending} onClick={() => route.mutate()}>
            {route.isPending ? "Routing…" : "Route this case"}
          </button>
        </div>
      ) : result.routed ? (
        /*
         * .outcome — a result panel, neutral. This is what happened, not an
         * announcement that something is good.
         */
        <div className="outcome">
          <h3>Routed</h3>
          <p>
            A registry office was resolved from the parcel and written to the case:{" "}
            <code>{result.registryDistrict}</code>. The case is now {result.status} and an
            attorney can be assigned to it.
          </p>
        </div>
      ) : (
        /*
         * .notice — TRUE, WORTH KNOWING, AND NOT A PROBLEM. Never [role="alert"].
         *
         * This is the normal production outcome today: the registry table is
         * empty until the Registrar General's dataset is seeded with provenance.
         * An alert here would tell an operator something went wrong when the
         * honest state is "we do not hold that reference data yet", and routing.ts
         * is explicit that an unresolved jurisdiction is OUR data gap — which is
         * exactly why the case goes to VALIDATING and never to NEEDS_INFO.
         *
         * IT DOES NOT SAY WHY, because the API does not say. Three different
         * causes — no active office for the district, several offices for it, or a
         * blank district on the parcel — arrive here identically. Naming one would
         * be stating a fact the platform did not derive.
         */
        <div className="notice">
          <h3>No registry office was resolved</h3>
          <p>
            The platform could not resolve a registry office for this parcel, so{" "}
            <strong>nothing was written to the case</strong> — no district has been guessed
            at. The case is at {result.status}, which is the ops queue.
          </p>
          <p>
            This screen is not able to tell you which of several possible reasons applied;
            the service does not report one, and this page will not invent it. Nothing is
            being asked of the client — an unresolved jurisdiction is the platform&rsquo;s
            own data gap, not something they left out.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * ASSIGNMENT: TWO DOORS, AND THE OPERATOR MUST BE ABLE TO SEE WHICH ONE THEY ARE
 * STANDING IN FRONT OF.
 * ===========================================================================
 *
 * WHAT WAS HERE, because the shape of the fix only makes sense against it. This
 * component fetched `/admin/professionals?status=verified`, rendered them in a
 * `<select>` labelled "Attorney to assign", and POSTed the chosen user id to
 * `/admin/cases/:id/assign` — an endpoint that READS NO BODY AT ALL and assigns
 * by panel rotation. So the operator picked one person, a different one was
 * assigned by the rule, and the screen reported success. Two separate defects in
 * one control:
 *
 *  1. THE SCREEN STATED A FACT THAT NEVER HAPPENED (CLAUDE.md non-negotiable 1).
 *     "Assign this attorney" under a dropdown says the operator chose. They did
 *     not, they cannot, and the whole point of ATTORNEY_BRIEF Part 1B is that
 *     they cannot. A UI that shows a choice which the server discards is not a
 *     cosmetic mismatch — it is the platform giving a false account of how work
 *     was allocated, in the one place that account matters.
 *  2. THE FETCHED LIST WAS A DIRECTORY (COMPLIANCE Constraint 1, Supreme Court
 *     Rules 1988 Rule 39). Named, comparable, verified attorneys, enumerated at
 *     the exact moment paid work is handed out. That is the shop window the panel
 *     design exists to remove, and Rule 39 applies inside the admin panel as much
 *     as at the client edge.
 *
 * SO THE MECHANICAL PATH PRESENTS NO CHOOSER. It states what will happen — the
 * next attorney in the rotation for THIS case's registry — and then reports who
 * actually received it, read from the server's response rather than from
 * anything this screen decided.
 *
 * THE OVERRIDE IS A DIFFERENT CONTROL AND LOOKS LIKE ONE. It is folded shut
 * behind a summary, it demands a reason code before it will send, and it says
 * what it costs: the row is stamped `admin_override`, and the record keeps who
 * the rotation would have chosen. It must never come to look like the ordinary
 * way to assign — an override that is one click closer than the rule is the rule.
 *
 * THERE IS NO PROFESSIONALS FETCH ANYWHERE IN THIS FILE. The override's assignee
 * is TYPED as a user id, carried over from the roster screen, exactly as a panel
 * seat is entered (AdminPanel's SeatForm makes the same trade for the same
 * reason). A picker would be one edit from being the directory again.
 */
function AssignAction({ caseRow }: { caseRow: Case }) {
  // ROUTED means the rotation has not run. ASSIGNED means it has, and the server
  // refuses a second roll (assertRotationHasNotRun) — so the mechanical control
  // is not drawn there, because the only honest act left is the override.
  const rotationIsAvailable = caseRow.status === "ROUTED";

  return (
    <div className="stack">
      {rotationIsAvailable ? <RotationAssign caseRow={caseRow} /> : null}
      <OverrideAssign caseRow={caseRow} folded={rotationIsAvailable} />
    </div>
  );
}

/** What both doors do to the people involved. Said once, used twice. */
function AccessConsequence() {
  return (
    <p>
      The attorney gets this case and <strong>every document on it</strong> — deeds,
      plans, identity papers — from the moment it is confirmed.
    </p>
  );
}

/**
 * THE MECHANICAL DOOR. One button, no fields, and no name anywhere before the
 * click — the same shape as RouteAction above, for the same reason: there is
 * nothing here for an operator to decide, so there is nothing here to decide it
 * with.
 *
 * The outcome reports the SERVER'S assignee. A screen that echoed an id it had
 * sent would report the operator's intent as the result; this endpoint is sent no
 * id at all, so the only truthful thing to print is what came back.
 */
function RotationAssign({ caseRow }: { caseRow: Case }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [assigned, setAssigned] = useState<Case | null>(null);

  const assign = useMutation({
    // NO BODY. The route reads none; sending one would be inventing a wire format
    // in which a hand-pick is expressible.
    mutationFn: async () =>
      apiFetch<Case>(`/admin/cases/${caseRow.id}/assign`, {
        token: await getToken(),
        method: "POST",
      }),
    onSuccess: (data) => {
      setAssigned(data);
      void queryClient.invalidateQueries({ queryKey: ["case", caseRow.id] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "cases"] });
    },
  });

  return (
    <div className="stack">
      <div className="consequence">
        <h3>Assigning an attorney</h3>
        <p>
          The case goes to the attorney whose turn it is on the panel for{" "}
          {caseRow.registryDistrict === null ? (
            "this case's registry"
          ) : (
            <code>{caseRow.registryDistrict}</code>
          )}{" "}
          — least recently assigned there. <strong>There is nothing to choose.</strong>{" "}
          You will be told who it landed on afterwards.
        </p>
        <AccessConsequence />
        <p>
          It runs <strong>once per case</strong>. Replacing them later is the override
          below, which records a reason. The{" "}
          <Link href="/admin/panel">panel</Link> is the pool it runs over; if nobody
          there can take it, the case waits here rather than going elsewhere.
        </p>
      </div>

      {/*
        The server's own message, verbatim. On this endpoint the 400s and 409s ARE
        the explanation — "must be routed before an attorney can be assigned", "the
        rotation has already run on this case", and the eligible-member conflict,
        which names the registry and counts the seats. Softening any of them would
        throw away the only useful thing in the response.
      */}
      {assign.error ? (
        <p role="alert">{apiErrorText(assign.error, "Could not assign this case.")}</p>
      ) : null}

      {assigned === null ? (
        <div className="actions">
          <button type="button" disabled={assign.isPending} onClick={() => assign.mutate()}>
            {assign.isPending ? "Assigning…" : "Assign by rotation"}
          </button>
        </div>
      ) : (
        <div className="outcome">
          <h3>Assigned</h3>
          <p>
            The rotation landed on user{" "}
            <code>{assigned.assignedProfessionalId ?? "— none returned"}</code>. The case
            is now {assigned.status}. Match the id on the{" "}
            <Link href="/admin/roster">roster</Link>.
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * THE DECLARED EXCEPTION.
 *
 * Folded shut while the rotation is available, so it cannot be mistaken for the
 * normal way to assign. On an already-assigned case it is open, because the
 * rotation has had its turn and this is the only remaining act — hiding the one
 * usable control behind a summary would read as "nothing can be done here".
 *
 * IT STATES ITS CONSEQUENCE BEFORE IT IS USED, which is the whole difference
 * between a recorded exception and a quiet hand-pick.
 */
function OverrideAssign({ caseRow, folded }: { caseRow: Case; folded: boolean }) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [assignee, setAssignee] = useState("");
  const [reason, setReason] = useState<AssignmentOverrideReason>("ASSIGNEE_DECLINED");
  const [clientError, setClientError] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<Case | null>(null);

  const isReplacement = caseRow.status === "ASSIGNED";

  const override = useMutation({
    mutationFn: async (body: { professionalUserId: string; reason: AssignmentOverrideReason }) =>
      apiFetch<Case>(`/admin/cases/${caseRow.id}/assign-override`, {
        token: await getToken(),
        method: "POST",
        body,
      }),
    onSuccess: (data) => {
      setAssigned(data);
      setAssignee("");
      void queryClient.invalidateQueries({ queryKey: ["case", caseRow.id] });
      void queryClient.invalidateQueries({ queryKey: ["admin", "cases"] });
    },
  });

  function submit() {
    setClientError(null);
    setAssigned(null);
    // The server's schema, not a second laxer one — see assignmentOverrideInputSchema.
    const parsed = assignmentOverrideInputSchema.safeParse({
      professionalUserId: assignee,
      reason,
    });
    if (!parsed.success) {
      const issue = parsed.error.issues[0];
      setClientError(issue ? `${issue.path.join(".")}: ${issue.message}` : "invalid");
      return;
    }
    override.mutate(parsed.data);
  }

  const body = (
    <div className="stack">
      <div className="consequence">
        <p>
          <strong>You name the attorney and the rule does not.</strong> The assignment is
          stored as an override with your reason, alongside who the rotation would have
          chosen. It is countable, and it is meant to be counted.
        </p>
        {/*
          ADDED WITH MIGRATION 0026 §1, and it is a real change to what this
          control can do rather than a caution. The override used to reach any
          verified attorney in the country; it now reaches the panel for this
          case's registry and nobody else. An override changes whose turn it is,
          not where the land is.
        */}
        <p>
          It still cannot leave the panel for this case&rsquo;s registry. If nobody there can
          take it, the case waits.
        </p>
        {isReplacement ? (
          <p>
            This case already has an attorney. Overriding{" "}
            <strong>revokes their assignment</strong> and their access with it. The status
            stays ASSIGNED, so what changes is the attorney, not the state.
          </p>
        ) : (
          <AccessConsequence />
        )}
      </div>

      <div className="field">
        <label htmlFor="override-assignee">Attorney&rsquo;s user id</label>
        <input
          type="text"
          id="override-assignee"
          value={assignee}
          onChange={(event) => setAssignee(event.target.value)}
        />
        <small className="field-hint">
          {/*
            TYPED, NOT PICKED, and the hint says why rather than leaving it to
            read as an unbuilt feature. Same trade as the panel seat form.
          */}
          From the <Link href="/admin/roster">roster</Link>. There is no list here to pick
          from: a roll of attorneys at the point of handing out work is a directory.
        </small>
      </div>

      <div className="field">
        <label htmlFor="override-reason">Why the rotation is being overridden</label>
        <select
          id="override-reason"
          value={reason}
          onChange={(event) => setReason(event.target.value as AssignmentOverrideReason)}
        >
          {ASSIGNMENT_OVERRIDE_REASONS.map((code) => (
            <option key={code} value={code}>
              {OVERRIDE_REASON_COPY[code]}
            </option>
          ))}
        </select>
        <small className="field-hint">
          A code, not a note — this reaches a record that cannot be erased, so it holds
          nothing about a person. No code means &ldquo;nobody is on the panel here&rdquo;:
          that case waits.
        </small>
      </div>

      {clientError ? <p role="alert">{clientError}</p> : null}
      {override.error ? (
        <p role="alert">{apiErrorText(override.error, "Could not override the rotation.")}</p>
      ) : null}

      <div className="actions">
        <button type="button" disabled={override.isPending} onClick={submit}>
          {override.isPending ? "Assigning…" : "Override and assign"}
        </button>
      </div>

      {assigned ? (
        /*
         * The success signal is the ASSIGNEE, not a status move: a replacement is
         * ASSIGNED -> ASSIGNED, which is not a transition, so a screen keyed off
         * the status would report a successful override as a no-op.
         */
        <div className="outcome">
          <h3>Assigned by override</h3>
          <p>
            The attorney on this case is now user{" "}
            <code>{assigned.assignedProfessionalId ?? "— none returned"}</code>, and the
            case is {assigned.status}. The override and its reason are on the record.
          </p>
        </div>
      ) : null}
    </div>
  );

  if (!folded) {
    return (
      <section className="stack">
        <h3>Assigning by override</h3>
        {body}
      </section>
    );
  }

  return (
    <details>
      <summary>Override the rotation (exceptional)</summary>
      {body}
    </details>
  );
}

/**
 * The override reason codes in an operator's words, keyed off the shared
 * constant so a code added there without copy here fails to compile rather than
 * showing someone a bare SCREAMING_SNAKE token to guess at.
 *
 * Every one of these describes THIS CASE or THIS ASSIGNEE'S AVAILABILITY. None
 * of them can be read as "we would rather this attorney had it", because that is
 * the steering mechanical assignment exists to prevent — see the warning on
 * ASSIGNMENT_OVERRIDE_REASONS.
 */
const OVERRIDE_REASON_COPY: Record<AssignmentOverrideReason, string> = {
  ASSIGNEE_DECLINED: "The attorney the rotation chose said no to this case",
  ASSIGNEE_UNAVAILABLE: "They cannot take it now, and not for long enough to pause their seat",
  CONFLICT_OF_INTEREST_DECLARED: "The attorney declared a conflict",
  PREEXISTING_CLIENT_RELATIONSHIP: "The client already instructs them on this matter",
  OPS_ESCALATION: "Ops had to act and none of the above fits",
};
